import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getStatistics,
  getTokenUsageOverTime,
  getRequestsOverTime,
  getAssistantUsageDistribution,
  getModelLatency,
  getCloudSpendOverTime,
  getRecentActivity,
  getModelDistribution,
  getSpendByAssistant,
  getUsageLogs,
} from '../services/api';
import { timeRangeCacheKey } from '../utils/timeRange';

const CACHE_TTL_MS = 55_000;
const CHART_POLL_MS = 30_000;
const ACTIVITY_POLL_MS = 5_000;
const EMPTY_USAGE_LOG_FILTERS = Object.freeze({});

const emptyLoading = () => ({
  statistics: true,
  tokenUsage: true,
  requests: true,
  cloudSpend: true,
  assistantUsage: true,
  modelLatency: true,
  recentActivity: true,
  modelDistribution: true,
  spendByAssistant: true,
  usageLogs: true,
});

const moduleCache = new Map();

function getCached(key) {
  const entry = moduleCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) return null;
  return entry.data;
}

function setCached(key, data) {
  moduleCache.set(key, { data, fetchedAt: Date.now() });
}

export function invalidateDashboardCache(timeRange, showAllPlatform) {
  moduleCache.delete(timeRangeCacheKey(timeRange, showAllPlatform));
}

function tabNeedsCharts(activeTab) {
  return ['overview', 'usage', 'spend', 'reliability'].includes(activeTab);
}

function tabNeedsSpendExtras(activeTab) {
  return ['overview', 'spend'].includes(activeTab);
}

function tabNeedsActivity(activeTab) {
  return ['overview', 'reliability'].includes(activeTab);
}

function applyPayload(setters, payload) {
  const {
    setStatistics,
    setTokenUsageData,
    setRequestsData,
    setCloudSpendData,
    setAssistantUsageData,
    setModelLatencyData,
    setRecentActivity,
    setRecentActivityTotal,
    setModelDistribution,
    setSpendByAssistant,
    setUsageLogs,
    setUsageLogsTotal,
  } = setters;
  if (payload.statistics !== undefined) setStatistics(payload.statistics);
  if (payload.tokenUsageData !== undefined) setTokenUsageData(payload.tokenUsageData);
  if (payload.requestsData !== undefined) setRequestsData(payload.requestsData);
  if (payload.cloudSpendData !== undefined) setCloudSpendData(payload.cloudSpendData);
  if (payload.assistantUsageData !== undefined) setAssistantUsageData(payload.assistantUsageData);
  if (payload.modelLatencyData !== undefined) setModelLatencyData(payload.modelLatencyData);
  if (payload.recentActivity !== undefined) setRecentActivity(payload.recentActivity);
  if (payload.recentActivityTotal !== undefined) setRecentActivityTotal(payload.recentActivityTotal);
  if (payload.modelDistribution !== undefined) setModelDistribution(payload.modelDistribution);
  if (payload.spendByAssistant !== undefined) setSpendByAssistant(payload.spendByAssistant);
  if (payload.usageLogs !== undefined) setUsageLogs(payload.usageLogs);
  if (payload.usageLogsTotal !== undefined) setUsageLogsTotal(payload.usageLogsTotal);
}

async function fetchStatisticsOnly(keycloak, timeRange, platformFlag) {
  try {
    return await getStatistics(keycloak, timeRange, platformFlag);
  } catch (e) {
    console.error('Failed to fetch statistics:', e);
    return null;
  }
}

async function fetchChartsBundle(keycloak, timeRange, platformFlag, { includeSpendExtras = false } = {}) {
  const promises = [
    getTokenUsageOverTime(keycloak, timeRange, platformFlag).catch(() => []),
    getRequestsOverTime(keycloak, timeRange, platformFlag).catch(() => []),
    getAssistantUsageDistribution(keycloak, timeRange, platformFlag).catch(() => []),
    getModelLatency(keycloak, timeRange, platformFlag).catch(() => []),
    getCloudSpendOverTime(keycloak, timeRange, platformFlag).catch(() => []),
  ];

  if (includeSpendExtras) {
    promises.push(
      getModelDistribution(keycloak, timeRange, platformFlag).catch(() => []),
      getSpendByAssistant(keycloak, timeRange, platformFlag).catch(() => []),
    );
  }

  const results = await Promise.all(promises);
  const [
    tokenData,
    requestsData,
    assistantData,
    latencyData,
    cloudSpendData,
    modelDistribution,
    spendByAssistant,
  ] = results;

  return {
    tokenUsageData: tokenData,
    requestsData,
    assistantUsageData: assistantData,
    modelLatencyData: latencyData,
    cloudSpendData,
    ...(includeSpendExtras ? { modelDistribution, spendByAssistant } : {}),
  };
}

async function fetchActivity(keycloak, page, pageSize, platformFlag) {
  try {
    const offset = (page - 1) * pageSize;
    const activityResponse = await getRecentActivity(keycloak, pageSize, offset, platformFlag);
    return {
      recentActivity: activityResponse.activities || [],
      recentActivityTotal: activityResponse.total || 0,
    };
  } catch (e) {
    console.error('Failed to fetch recent activity:', e);
    return { recentActivity: [], recentActivityTotal: 0 };
  }
}

async function fetchUsageLogs(keycloak, timeRange, platformFlag, filters, page, pageSize) {
  try {
    const offset = (page - 1) * pageSize;
    return await getUsageLogs(keycloak, {
      timeRange,
      showAll: platformFlag,
      limit: pageSize,
      offset,
      ...filters,
    });
  } catch (e) {
    console.error('Failed to fetch usage logs:', e);
    return { items: [], total: 0 };
  }
}

export function useDashboardStats({
  keycloak,
  rolesLoaded,
  isAdmin,
  showAll,
  timeRange,
  recentActivityPage,
  recentActivityPageSize,
  activeTab = 'overview',
  usageLogFilters = EMPTY_USAGE_LOG_FILTERS,
  usageLogPage = 1,
  usageLogPageSize = 20,
}) {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(emptyLoading());
  const [tokenUsageData, setTokenUsageData] = useState([]);
  const [requestsData, setRequestsData] = useState([]);
  const [cloudSpendData, setCloudSpendData] = useState([]);
  const [assistantUsageData, setAssistantUsageData] = useState([]);
  const [modelLatencyData, setModelLatencyData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentActivityTotal, setRecentActivityTotal] = useState(0);
  const [modelDistribution, setModelDistribution] = useState([]);
  const [spendByAssistant, setSpendByAssistant] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageLogsTotal, setUsageLogsTotal] = useState(0);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchGen = useRef(0);
  const activityPageInitialized = useRef(false);
  const skipActivityPageEffect = useRef(true);

  const platformFlag = showAll && isAdmin;

  const setters = {
    setStatistics,
    setTokenUsageData,
    setRequestsData,
    setCloudSpendData,
    setAssistantUsageData,
    setModelLatencyData,
    setRecentActivity,
    setRecentActivityTotal,
    setModelDistribution,
    setSpendByAssistant,
    setUsageLogs,
    setUsageLogsTotal,
  };

  const load = useCallback(
    async ({
      force = false,
      showLoading = true,
      includeCharts = tabNeedsCharts(activeTab),
      includeActivity = tabNeedsActivity(activeTab),
      includeSpendExtras = tabNeedsSpendExtras(activeTab),
      includeUsageLogs = activeTab === 'spend',
      activityPage = recentActivityPage,
    } = {}) => {
      if (!keycloak?.authenticated || !rolesLoaded) return;

      const key = timeRangeCacheKey(timeRange, platformFlag);
      const cached = !force ? getCached(key) : null;

      if (cached && !includeUsageLogs) {
        applyPayload(setters, cached);
        if (showLoading) {
          setLoading({
            statistics: false,
            tokenUsage: false,
            requests: false,
            cloudSpend: false,
            assistantUsage: false,
            modelLatency: false,
            recentActivity: false,
            modelDistribution: false,
            spendByAssistant: false,
            usageLogs: false,
          });
        }
        if (!force) {
          load({
            force: true,
            showLoading: false,
            includeCharts,
            includeActivity,
            includeSpendExtras,
            includeUsageLogs,
            activityPage,
          });
          return;
        }
      }

      const gen = ++fetchGen.current;

      if (showLoading) {
        setLoading((prev) => ({
          ...prev,
          statistics: true,
          ...(includeCharts
            ? {
                tokenUsage: true,
                requests: true,
                cloudSpend: true,
                assistantUsage: true,
                modelLatency: true,
                modelDistribution: includeSpendExtras,
                spendByAssistant: includeSpendExtras,
              }
            : {}),
          ...(includeActivity ? { recentActivity: true } : {}),
          ...(includeUsageLogs ? { usageLogs: true } : {}),
        }));
      }

      try {
        const statsPromise = fetchStatisticsOnly(keycloak, timeRange, platformFlag);
        const chartsPromise = includeCharts
          ? fetchChartsBundle(keycloak, timeRange, platformFlag, { includeSpendExtras })
          : Promise.resolve(null);
        const activityPromise = includeActivity
          ? fetchActivity(keycloak, activityPage, recentActivityPageSize, platformFlag)
          : Promise.resolve(null);
        const logsPromise = includeUsageLogs
          ? fetchUsageLogs(keycloak, timeRange, platformFlag, usageLogFilters, usageLogPage, usageLogPageSize)
          : Promise.resolve(null);

        const [stats, charts, activity, logs] = await Promise.all([
          statsPromise,
          chartsPromise,
          activityPromise,
          logsPromise,
        ]);

        if (gen !== fetchGen.current) return;

        const payload = {
          statistics: stats,
          ...(charts || {}),
          ...(activity || {}),
          ...(logs ? { usageLogs: logs.items, usageLogsTotal: logs.total } : {}),
        };

        applyPayload(setters, payload);
        setLastFetchedAt(Date.now());
        if (stats === null) {
          setFetchError('Failed to refresh dashboard statistics');
        } else {
          setFetchError(null);
        }

        if (includeCharts && includeActivity && !includeUsageLogs) {
          setCached(key, {
            statistics: stats,
            tokenUsageData: charts?.tokenUsageData ?? [],
            requestsData: charts?.requestsData ?? [],
            cloudSpendData: charts?.cloudSpendData ?? [],
            assistantUsageData: charts?.assistantUsageData ?? [],
            modelLatencyData: charts?.modelLatencyData ?? [],
            recentActivity: activity?.recentActivity ?? [],
            recentActivityTotal: activity?.recentActivityTotal ?? 0,
            modelDistribution: charts?.modelDistribution ?? [],
            spendByAssistant: charts?.spendByAssistant ?? [],
          });
        }
      } catch (e) {
        if (gen !== fetchGen.current) return;
        setFetchError(e?.message || 'Failed to refresh dashboard data');
      }

      if (showLoading) {
        setLoading((prev) => ({
          statistics: false,
          tokenUsage: includeCharts ? false : prev.tokenUsage,
          requests: includeCharts ? false : prev.requests,
          cloudSpend: includeCharts ? false : prev.cloudSpend,
          assistantUsage: includeCharts ? false : prev.assistantUsage,
          modelLatency: includeCharts ? false : prev.modelLatency,
          modelDistribution: includeCharts && includeSpendExtras ? false : prev.modelDistribution,
          spendByAssistant: includeCharts && includeSpendExtras ? false : prev.spendByAssistant,
          recentActivity: includeActivity ? false : prev.recentActivity,
          usageLogs: includeUsageLogs ? false : prev.usageLogs,
        }));
      } else {
        setLoading((prev) => ({
          ...prev,
          statistics: false,
          ...(includeCharts
            ? {
                tokenUsage: false,
                requests: false,
                cloudSpend: false,
                assistantUsage: false,
                modelLatency: false,
                modelDistribution: includeSpendExtras ? false : prev.modelDistribution,
                spendByAssistant: includeSpendExtras ? false : prev.spendByAssistant,
              }
            : {}),
          ...(includeActivity ? { recentActivity: false } : {}),
          ...(includeUsageLogs ? { usageLogs: false } : {}),
        }));
      }
    },
    [
      keycloak,
      rolesLoaded,
      platformFlag,
      timeRange,
      recentActivityPage,
      recentActivityPageSize,
      activeTab,
      usageLogFilters,
      usageLogPage,
      usageLogPageSize,
    ]
  );

  const loadActivityPage = useCallback(
    async (page) => {
      if (!keycloak?.authenticated || !rolesLoaded) return;
      const gen = ++fetchGen.current;
      setLoading((prev) => ({ ...prev, recentActivity: true }));
      try {
        const activity = await fetchActivity(
          keycloak,
          page,
          recentActivityPageSize,
          platformFlag,
        );
        if (gen !== fetchGen.current) return;
        applyPayload(setters, activity);
        setFetchError(null);
      } catch (e) {
        if (gen !== fetchGen.current) return;
        setFetchError(e?.message || 'Failed to refresh activity');
      }
      setLoading((prev) => ({ ...prev, recentActivity: false }));
    },
    [keycloak, rolesLoaded, platformFlag, recentActivityPageSize]
  );

  useEffect(() => {
    if (!rolesLoaded) return;
    skipActivityPageEffect.current = true;
    load({
      showLoading: true,
      includeCharts: tabNeedsCharts(activeTab),
      includeActivity: tabNeedsActivity(activeTab),
      includeSpendExtras: tabNeedsSpendExtras(activeTab),
      includeUsageLogs: activeTab === 'spend',
    });
    activityPageInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load identity tracks fetch inputs via its own deps
  }, [rolesLoaded, timeRange, platformFlag, activeTab]);

  useEffect(() => {
    if (!keycloak?.authenticated || !rolesLoaded) return;

    const fastId = setInterval(() => {
      if (!tabNeedsActivity(activeTab)) return;
      load({
        force: true,
        showLoading: false,
        includeCharts: false,
        includeActivity: true,
      }).catch(() => {});
    }, ACTIVITY_POLL_MS);

    const chartsId = setInterval(() => {
      if (!tabNeedsCharts(activeTab)) return;
      load({
        force: true,
        showLoading: false,
        includeCharts: true,
        includeActivity: false,
        includeSpendExtras: tabNeedsSpendExtras(activeTab),
      }).catch(() => {});
    }, CHART_POLL_MS);

    return () => {
      clearInterval(fastId);
      clearInterval(chartsId);
    };
  }, [keycloak?.authenticated, rolesLoaded, timeRange, platformFlag, activeTab, load]);

  useEffect(() => {
    if (!activityPageInitialized.current) return;
    if (skipActivityPageEffect.current) {
      skipActivityPageEffect.current = false;
      return;
    }
    loadActivityPage(recentActivityPage);
  }, [recentActivityPage, loadActivityPage]);

  return {
    statistics,
    loading,
    tokenUsageData,
    requestsData,
    cloudSpendData,
    assistantUsageData,
    modelLatencyData,
    recentActivity,
    recentActivityTotal,
    modelDistribution,
    spendByAssistant,
    usageLogs,
    usageLogsTotal,
    lastFetchedAt,
    fetchError,
    load,
    loadActivityPage,
  };
}
